import React, { ReactElement, useContext } from 'react';
import { Filter } from '@fabric-ui/components';
import { flex, shadowInsetBorder } from '@fabric-ui/theme';
import { emptyObject, noop } from '@fabric-ui/utils';
import { Box, LinearProgress, SxProps } from '@mui/material';
import { ChartClickInteraction } from '../../../components/Widgets/types';
import { AsyncQueryStatus } from '../../../hooks/queryObjectAsync/useQueryObjectAsyncWithCachedResults.utils';
import useElementOnScreen from '../../../hooks/useElementOnScreen';
import { CustomDashboardContext } from '../CustomDashboardContext';
import { useGetWidgetData } from '../hooks/useGetWidgetData';
import useTableViewWidget from '../hooks/useTableViewWidget';
import { WidgetCategory } from '../types';
import { AllTypeWidgets } from '../types/combined.types';
import { TableTypeWidget } from '../types/TableWidget.types';
import { getParamFromURL } from './Clickthrough/utils';
import Widget from './Widget/Widget';
import FloatingWidgetLoadingState from './Widget/WidgetHistoricLoadingState/FloatingWidgetLoadingState';
import TableWidget from './Widgets/TableWidget';
import WidgetToolbar from './WidgetToolbar';

interface WidgetProps {
  widget: AllTypeWidgets;
  setWidget?: (newWidget: any) => void;
  globalFilters?: Filter;
  children: any;
  showToolbar?: boolean;
  grabbable?: boolean;
  customToolbar?: ReactElement<any>;
  isTable?: boolean;
  setIsTable?: any;
  selected?: any;
  onSelect?: (v) => void;
  clickInteractions?: ChartClickInteraction[];
  sx?: SxProps;
  enableResponsiveLegend?: boolean;
  background?: string;
}

const DraggableWidget: React.FC<WidgetProps> = React.memo(
  ({
    widget,
    setWidget = noop,
    globalFilters,
    children,
    showToolbar = true,
    grabbable = false,
    isTable,
    setIsTable,
    customToolbar,
    selected,
    onSelect,
    clickInteractions,
    sx = emptyObject,
    background,
    enableResponsiveLegend,
  }) => {
    const { filters, scrollContainerRef } = useContext(CustomDashboardContext);
    const isEditMode = getParamFromURL<boolean>(filters.isEditMode);
    const { ref, isVisible } = useElementOnScreen<HTMLDivElement>({ root: scrollContainerRef.current });
    const { isRefetching, responseCalculatedAt, isAsyncQuery, asyncQueryStatus, queryId, widgetQueryResponse } = useGetWidgetData({
      widget,
      contextFilter: globalFilters,
      visible: isVisible,
      debounceQueries: false,
    });
    const resolvedToolbar = customToolbar ?? (
      <WidgetToolbar
        widget={widget}
        setWidget={setWidget}
        isTable={isTable}
        setIsTable={setIsTable}
        queryId={queryId}
        widgetQueryResponse={widgetQueryResponse}
      />
    );
    const { tableViewWidget, setTableViewWidget } = useTableViewWidget(widget);

    const wasTable = React.useRef(false);
    React.useEffect(() => {
      if (isTable) wasTable.current = true;
    }, [isTable]);

    const wasVisible = React.useRef(false);
    if (isVisible) wasVisible.current = true;

    return (
      <>
        {isRefetching && <LinearProgress sx={{ position: 'absolute', width: '100%', height: '2px' }} />}
        <FloatingWidgetLoadingState
          ref={ref}
          isLoading={asyncQueryStatus === AsyncQueryStatus.Refreshing}
          dataUpdateAt={
            asyncQueryStatus === AsyncQueryStatus.Refreshing && responseCalculatedAt ? new Date(responseCalculatedAt) : undefined
          }
          enabled={isAsyncQuery}
          sx={{
            pl: 3,
            pr: 2,
            pt: 2,
            height: '100%',
            boxShadow: background === 'transparent' ? undefined : shadowInsetBorder,
            ...flex.col,
            cursor: isEditMode && grabbable ? 'grab' : 'default',
            ...([WidgetCategory.Tile, WidgetCategory.Text].includes(widget.category) ? { overflow: 'hidden' } : {}),
            ...sx,
          }}>
          {showToolbar && resolvedToolbar}
          {(isTable || wasTable.current) && (
            <Box sx={{ display: isTable && isVisible ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <Widget
                widget={tableViewWidget}
                setWidget={setTableViewWidget}
                isEdit={false}
                visible={isTable && isVisible}
                globalFilters={globalFilters}>
                <TableWidget widget={tableViewWidget as TableTypeWidget} setWidget={setTableViewWidget} />
              </Widget>
            </Box>
          )}
          {!isTable && (isVisible || wasVisible.current) && (
            <Widget
              widget={widget}
              setWidget={setWidget}
              isEdit={false}
              visible={isVisible}
              globalFilters={globalFilters}
              selected={selected}
              onSelect={onSelect}
              clickInteractions={clickInteractions}
              enableResponsiveLegend={enableResponsiveLegend}>
              {children}
            </Widget>
          )}
        </FloatingWidgetLoadingState>
      </>
    );
  }
);

export default DraggableWidget;
